import { useJobillaOrgId } from '@/hooks'
import {
	Button,
	ConfirmationDialog,
	type ConfirmationDialogRef,
	emitUnhandledError,
	logger,
	RichLiveField,
	type RichLiveFieldRef,
	SettingsHeading,
	SettingsNav,
} from '@/ui'
import {
	emailTemplatesDestroy,
	emailTemplatesIndexQueryKey,
	useEmailTemplatesIndex,
	useEmailTemplatesStore,
	useEmailTemplatesUpdate,
} from '@jobilla/nexus-sdk'
import type { EmailTemplateData } from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { CheckIcon, Trash2Icon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

export function EmailTemplatesSettings() {
	const organisationId = useJobillaOrgId()

	const queryClient = useQueryClient()

	const { data: templatesResponse, isPending: isLoading } = useEmailTemplatesIndex(
		{ 'filter[organisation_id]': organisationId, sort: ['name'] },
		{ enabled: !!organisationId },
	)

	const templates = useMemo(() => templatesResponse ?? [], [templatesResponse])

	const [selectedId, setSelectedId] = useState<string | null>(null)
	const [freshlyCreatedId, setFreshlyCreatedId] = useState<string | null>(null)
	const confirmRef = useRef<ConfirmationDialogRef>(null)

	useEffect(() => {
		if (selectedId === null && templates.length > 0) {
			setSelectedId(templates[0].id)
		}
		if (selectedId && !templates.find((t) => t.id === selectedId)) {
			setSelectedId(templates.length > 0 ? templates[0].id : null)
		}
	}, [selectedId, templates])

	const selectedTemplate = useMemo(
		() => templates.find((t) => t.id === selectedId) ?? null,
		[templates, selectedId],
	)

	const invalidateTemplates = useCallback(() => {
		return queryClient.invalidateQueries({
			queryKey: emailTemplatesIndexQueryKey({ 'filter[organisation_id]': organisationId }),
		})
	}, [queryClient, organisationId])

	const handleDeleteTemplate = useCallback(async (id: string) => {
		try {
			await confirmRef.current?.confirm({
				title: 'Delete template',
				message: 'Are you sure you want to delete this template? This action cannot be undone.',
				confirmText: 'Delete',
				cancelText: 'Cancel',
			})
		} catch {
			return
		}
		try {
			await emailTemplatesDestroy(id)
			if (selectedId === id) { setSelectedId(null) }
			await invalidateTemplates()
		} catch (err) {
			logger.error(err, {
				tags: { component: 'EmailTemplateSettings', action: 'delete-template' },
			})
			emitUnhandledError({
				title: 'Failed to delete template',
				description: err instanceof Error ? err.message : 'An unexpected error occurred.',
				cause: err,
			})
		}
	}, [invalidateTemplates, selectedId])

	return (
		<SettingsNav active="email-templates" fullWidth fixedHeight>
			<div className="grid grid-cols-[280px_1fr] h-full">
				<TemplateList
					templates={templates}
					isLoading={isLoading}
					selectedId={selectedId}
					onSelect={setSelectedId}
					onDelete={handleDeleteTemplate}
					organisationId={organisationId}
					onCreated={async (id) => {
						await invalidateTemplates()
						setSelectedId(id)
						setFreshlyCreatedId(id)
					}}
				/>
				{selectedTemplate
					? (
						<TemplateEditor
							key={selectedTemplate.id}
							template={selectedTemplate}
							onSaved={invalidateTemplates}
							autoFocus={selectedTemplate.id === freshlyCreatedId}
						/>
					)
					: (
						<div className="flex items-center justify-center text-gray-400 text-sm">
							{isLoading ? 'Loading...' : 'Select a template or create a new one'}
						</div>
					)}
			</div>
			<ConfirmationDialog ref={confirmRef} />
		</SettingsNav>
	)
}

function TemplateList({
	templates,
	isLoading,
	selectedId,
	onSelect,
	onDelete,
	organisationId,
	onCreated,
}: {
	templates: EmailTemplateData[]
	isLoading: boolean
	selectedId: string | null
	onSelect: (id: string) => void
	onDelete: (id: string) => void
	organisationId: string
	onCreated: (id: string) => void
}) {
	const { t } = useTranslation('common')
	const { mutateAsync: createTemplate, isPending: isCreating } = useEmailTemplatesStore()

	const handleCreate = useCallback(async () => {
		try {
			const result = await createTemplate({
				data: {
					organisationId: organisationId,
					name: t('emailTemplates.newTemplate'),
					subject: '',
					body: '',
				},
			})
			onCreated(result.id)
		} catch (err) {
			logger.error(err, {
				tags: { component: 'EmailTemplateSettings', action: 'create-template' },
			})
			emitUnhandledError({
				title: t('emailTemplates.failedToCreate'),
				description: err instanceof Error ? err.message : t('emailTemplates.unexpectedError'),
				cause: err,
			})
		}
	}, [createTemplate, organisationId, onCreated, t])

	return (
		<div className="border-r border-gray-100 h-full overflow-y-auto">
			<div className="p-4 border-b border-gray-100">
				<SettingsHeading>{t('emailTemplates.title')}</SettingsHeading>
				<p className="text-sm text-gray-500 mt-1">
					{t('emailTemplates.description')}
				</p>
			</div>
			<div className="p-3">
				<Button
					fullWidth
					size="short"
					type="committal"
					onClick={handleCreate}
					isLoading={isCreating}
					prefix="plus"
				>
					{t('emailTemplates.newTemplate')}
				</Button>
			</div>
			{isLoading
				? <div className="p-4 text-sm text-gray-400">{t('common.loading')}</div>
				: (
					<div className="flex flex-col">
						{templates.map((template) => (
							<div
								key={template.id}
								className={`group flex items-center border-b border-gray-50 transition-colors ${
									template.id === selectedId
										? 'bg-brand-50 text-brand-700'
										: 'hover:bg-gray-50 text-gray-700'
								}`}
							>
								<button
									type="button"
									onClick={() => onSelect(template.id)}
									className="flex-1 text-left px-4 py-3 min-w-0"
								>
									<div className="text-sm font-medium truncate">{template.name}</div>
									{template.subject && (
										<div className="text-xs text-gray-400 truncate mt-0.5">{template.subject}</div>
									)}
								</button>
								<button
									type="button"
									onClick={(e) => {
										e.stopPropagation()
										onDelete(template.id)
									}}
									className="p-2 mr-2 text-gray-300 hover:text-red-500 transition-colors cursor-pointer"
									aria-label={`Delete ${template.name}`}
								>
									<Trash2Icon className="size-3.5" />
								</button>
							</div>
						))}
					</div>
				)}
		</div>
	)
}

const PLACEHOLDERS = [
	{ key: '{{candidate_name}}', label: 'Candidate name' },
	{ key: '{{candidate_first_name}}', label: 'First name' },
	{ key: '{{candidate_email}}', label: 'Candidate email' },
	{ key: '{{campaign_title}}', label: 'Campaign title' },
	{ key: '{{campaign_location}}', label: 'Campaign location' },
	{ key: '{{sender_name}}', label: 'Sender name' },
	{ key: '{{sender_email}}', label: 'Sender email' },
	{ key: '{{organisation_name}}', label: 'Organisation name' },
]

function TemplateEditor({
	template,
	onSaved,
	autoFocus,
}: {
	template: EmailTemplateData
	onSaved: () => void
	autoFocus?: boolean
}) {
	const [name, setName] = useState(template.name)
	const [subject, setSubject] = useState(template.subject ?? '')
	const [body, setBody] = useState(template.body)
	const [showPlaceholders, setShowPlaceholders] = useState(false)
	const nameInputRef = useRef<HTMLInputElement>(null)
	const editorRef = useRef<RichLiveFieldRef>(null)
	const placeholderRef = useRef<HTMLDivElement>(null)
	const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	useEffect(() => {
		if (autoFocus && nameInputRef.current) {
			nameInputRef.current.focus()
			nameInputRef.current.select()
		}
	}, [autoFocus])

	useEffect(() => {
		if (!showPlaceholders) { return }
		const handleClickOutside = (e: MouseEvent) => {
			if (placeholderRef.current && !placeholderRef.current.contains(e.target as Node)) {
				setShowPlaceholders(false)
			}
		}
		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [showPlaceholders])

	useEffect(() => {
		return () => {
			if (savedTimerRef.current) { clearTimeout(savedTimerRef.current) }
		}
	}, [])

	const { mutateAsync: updateTemplate, isPending: isSaving } = useEmailTemplatesUpdate()
	const [showSaved, setShowSaved] = useState(false)

	const hasChanges = name !== template.name || subject !== (template.subject ?? '') || body !== template.body

	const handleSave = useCallback(async () => {
		try {
			await updateTemplate({
				emailTemplate: template.id,
				data: {
					name,
					subject: subject || null,
					body,
				},
			})
			onSaved()
			setShowSaved(true)
			if (savedTimerRef.current) { clearTimeout(savedTimerRef.current) }
			savedTimerRef.current = setTimeout(() => setShowSaved(false), 2000)
		} catch (err) {
			logger.error(err, {
				tags: { component: 'EmailTemplateSettings', action: 'save-template' },
			})
			emitUnhandledError({
				title: 'Failed to save template',
				description: err instanceof Error ? err.message : 'An unexpected error occurred.',
				cause: err,
			})
		}
	}, [updateTemplate, name, subject, body, onSaved])

	const insertPlaceholder = useCallback((placeholder: string) => {
		editorRef.current?.insertText(placeholder)
		setShowPlaceholders(false)
	}, [])

	return (
		<div className="overflow-y-auto h-full">
			<div className="p-6 space-y-4">
				<div>
					<label htmlFor="template-name" className="block text-sm font-medium text-gray-700 mb-1">
						Template name
					</label>
					<input
						ref={nameInputRef}
						id="template-name"
						type="text"
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder="e.g. Rejection letter"
						className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all"
					/>
				</div>

				<div>
					<label htmlFor="template-subject" className="block text-sm font-medium text-gray-700 mb-1">
						Subject line
					</label>
					<input
						id="template-subject"
						type="text"
						value={subject}
						onChange={(e) => setSubject(e.target.value)}
						placeholder="Email subject (optional)"
						className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all"
					/>
				</div>

				<div>
					<div className="flex items-center justify-between mb-1">
						<label className="block text-sm font-medium text-gray-700">Body</label>
						<div ref={placeholderRef} className="relative">
							<button
								type="button"
								onClick={() => setShowPlaceholders(!showPlaceholders)}
								className="text-xs text-brand-600 hover:text-brand-700 font-medium"
							>
								Insert placeholder
							</button>
							{showPlaceholders && (
								<div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 w-56">
									{PLACEHOLDERS.map((p) => (
										<button
											key={p.key}
											type="button"
											onClick={() => insertPlaceholder(p.key)}
											className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors first:rounded-t-lg last:rounded-b-lg"
										>
											<span className="text-gray-700">{p.label}</span>
											<span className="block text-xs text-gray-400 font-mono">{p.key}</span>
										</button>
									))}
								</div>
							)}
						</div>
					</div>
					<div className="border border-gray-200 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-brand-500/20 focus-within:border-brand-500 transition-all min-h-[300px]">
						<RichLiveField
							ref={editorRef}
							value={body}
							onChange={setBody}
							outputFormat="html"
							disableMedia
							showStaticToolbar
						/>
					</div>
				</div>
			</div>

			<div className="flex items-center justify-end gap-3 px-6 py-4">
				{showSaved && (
					<span className="flex items-center gap-1 text-sm text-green-600">
						<CheckIcon className="size-4" />
						Saved
					</span>
				)}
				<Button
					type="committal"
					size="short"
					onClick={handleSave}
					disabled={!hasChanges || !name.trim()}
					isLoading={isSaving}
				>
					Save
				</Button>
			</div>
		</div>
	)
}
